import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { SCHOOL_LEVELS, SUBJECTS, type RendezVousKind, type SchoolLevel } from '@jeraisonne/db';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { TutorBookingTabs } from '@/components/tutor-booking-tabs';
import {
  useCancelTutorBookingSlot,
  useCreateTutorBookingRule,
  useSaveTutorBookingDefaults,
  useTutorBookingDefaults,
  useTutorBookingRules,
  useTutorBookingSlots,
  useUpdateTutorBookingRule,
  type TutorBookingRule,
} from '@/lib/tutor/booking';

// Colonnes affichées Lundi → Dimanche ; `weekday` stocké en base reste
// 0=dimanche..6=samedi (convention Date#getUTCDay, cf. migration 0215).
const DAYS = [
  { weekday: 1, label: 'Lun' },
  { weekday: 2, label: 'Mar' },
  { weekday: 3, label: 'Mer' },
  { weekday: 4, label: 'Jeu' },
  { weekday: 5, label: 'Ven' },
  { weekday: 6, label: 'Sam' },
  { weekday: 0, label: 'Dim' },
] as const;

const WEEKDAY_FULL_LABEL: Record<number, string> = {
  0: 'dimanche',
  1: 'lundi',
  2: 'mardi',
  3: 'mercredi',
  4: 'jeudi',
  5: 'vendredi',
  6: 'samedi',
};

const HOURS = Array.from({ length: 15 }, (_, i) => 7 + i); // 7h..21h
const DURATIONS = [30, 45, 60, 90, 120];

function hourStartTime(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00:00`;
}

function formatPrice(cents: number): string {
  return (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

function formatSlotDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function useChaptersForLevel(level: SchoolLevel | '') {
  return useQuery({
    queryKey: ['chapters-for-level', level],
    enabled: !!level,
    queryFn: async (): Promise<Array<{ id: string; title: string; subject: string }>> => {
      const { data, error } = await supabase
        .from('chapters')
        .select('id, title, subject')
        .eq('level', level as SchoolLevel)
        .eq('status', 'published')
        .order('subject')
        .order('display_order');
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

function FormField({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="font-display text-ink-700 mb-1.5 block text-xs font-semibold"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

/**
 * Valide le prix du profil de créneau récurrent selon le type. `cours` reste
 * toujours payant (1-500€, migration 0243) ; `etude` accepte 0€ (gratuite) OU
 * 1-500€, au choix du tuteur. Retourne le message d'erreur à afficher, ou
 * `null` si le prix est valide.
 */
export function validateBookingPriceCents(kind: 'cours' | 'etude', cents: number): string | null {
  if (!Number.isFinite(cents) || cents < 0) return 'Prix invalide.';
  if (cents > 50000) return 'Le prix ne peut pas dépasser 500 €.';
  if (kind === 'cours' && cents < 100) {
    return 'Un cours ne peut pas être gratuit — le prix doit être d’au moins 1 €.';
  }
  if (kind === 'etude' && cents !== 0 && cents < 100) {
    return 'Le prix doit être 0 € (gratuite) ou d’au moins 1 €.';
  }
  return null;
}

const SELECT_CLASS =
  'border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-11 w-full appearance-none rounded-xl border px-3.5 font-sans text-sm transition-colors focus:outline-none';
const INPUT_CLASS =
  'border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-11 w-full rounded-xl border px-3.5 font-sans text-sm transition-colors focus:outline-none';

// ── Profil de cours (réglage global, appliqué à toutes les cases cochées) ────

function CourseProfilePanel({
  tutorId,
  kind,
  onKindChange,
}: {
  tutorId: string;
  kind: 'cours' | 'etude';
  onKindChange: (kind: 'cours' | 'etude') => void;
}) {
  const { data: defaults, isLoading } = useTutorBookingDefaults(tutorId);
  const save = useSaveTutorBookingDefaults(tutorId);

  const [level, setLevel] = useState<SchoolLevel | ''>('');
  const [chapterId, setChapterId] = useState('');
  const [capacity, setCapacity] = useState(1);
  const [priceEuros, setPriceEuros] = useState('20');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const { data: chapters = [] } = useChaptersForLevel(level);

  // Pré-remplit le formulaire depuis le profil enregistré, une seule fois
  // (édition locale ensuite indépendante d'un refetch en arrière-plan).
  // `!= null` (pas `if (defaults.price_cents)`) : 0€ est une valeur valide
  // pour une étude gratuite, pas juste "pas encore de valeur".
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (defaults && !hydratedRef.current) {
      setLevel(defaults.level ?? '');
      setChapterId(defaults.chapter_id ?? '');
      setCapacity(defaults.capacity ?? 1);
      if (defaults.price_cents != null) setPriceEuros((defaults.price_cents / 100).toString());
      hydratedRef.current = true;
    }
  }, [defaults]);

  const subjectLabel = (slug: string) => SUBJECTS.find((s) => s.value === slug)?.label ?? slug;

  function handleKindChange(k: 'cours' | 'etude') {
    if (k === kind) return;
    // Bascule vers étude : suggère gratuit par défaut (le tuteur peut ensuite
    // facturer s'il veut). Bascule vers cours : un cours ne peut jamais être
    // gratuit (migration 0243) — remonte à une suggestion payante si besoin.
    if (k === 'etude') {
      setPriceEuros('0');
    } else if (Number(priceEuros.replace(',', '.')) < 1) {
      setPriceEuros('20');
    }
    onKindChange(k);
  }

  const handleSave = async () => {
    setError(null);
    setSaved(false);
    if (!level) {
      setError('Choisis un niveau.');
      return;
    }
    const cents = Math.round(Number(priceEuros.replace(',', '.')) * 100);
    const priceError = validateBookingPriceCents(kind, cents);
    if (priceError) {
      setError(priceError);
      return;
    }
    try {
      await save.mutateAsync({
        level,
        chapter_id: chapterId || null,
        capacity,
        price_cents: cents,
        kind,
      });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enregistrement impossible.');
    }
  };

  return (
    <div className="border-ink-100 space-y-3 rounded-2xl border bg-white p-4">
      <div>
        <p className="text-ink-900 text-sm font-semibold">Profil du créneau récurrent</p>
        <p className="text-ink-500 mt-0.5 text-xs">
          Type, niveau, chapitre, places et prix appliqués à tous les créneaux ci-dessous.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <div className="border-primary-400 h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      ) : (
        <>
          <div className="flex gap-2">
            {(['cours', 'etude'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => handleKindChange(k)}
                className={cn(
                  'min-h-[2.75rem] flex-1 rounded-xl border px-3 text-sm font-semibold transition-colors active:scale-[0.98]',
                  kind === k
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-ink-200 text-ink-600 bg-white',
                )}
              >
                {k === 'cours' ? 'Cours (payant)' : "Session d'étude"}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Niveau" htmlFor="default-level">
              <select
                id="default-level"
                value={level}
                onChange={(e) => {
                  setLevel(e.target.value as SchoolLevel | '');
                  setChapterId('');
                }}
                className={SELECT_CLASS}
              >
                <option value="">— Choisir —</option>
                {SCHOOL_LEVELS.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Places max" htmlFor="default-capacity">
              <input
                id="default-capacity"
                type="number"
                min={1}
                max={8}
                value={capacity}
                onChange={(e) => setCapacity(Math.min(8, Math.max(1, Number(e.target.value) || 1)))}
                className={INPUT_CLASS}
              />
            </FormField>
          </div>

          <FormField label="Chapitre (optionnel)" htmlFor="default-chapter">
            <select
              id="default-chapter"
              value={chapterId}
              onChange={(e) => setChapterId(e.target.value)}
              disabled={!level}
              className={SELECT_CLASS}
            >
              <option value="">— Aucun chapitre précis —</option>
              {chapters.map((c) => (
                <option key={c.id} value={c.id}>
                  {subjectLabel(c.subject)} — {c.title}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Prix par élève (€)" htmlFor="default-price">
            <input
              id="default-price"
              type="text"
              inputMode="decimal"
              value={priceEuros}
              onChange={(e) => setPriceEuros(e.target.value)}
              className={INPUT_CLASS}
            />
            {kind === 'etude' && (
              <p className="text-ink-500 mt-1 font-sans text-xs">0 € = session gratuite.</p>
            )}
          </FormField>

          {error && <p className="text-accent-600 font-sans text-xs">{error}</p>}
          {saved && !error && <p className="text-xp-600 font-sans text-xs">Profil enregistré.</p>}

          <Button onClick={handleSave} disabled={save.isPending} className="w-full">
            {save.isPending ? 'Enregistrement…' : 'Enregistrer le profil'}
          </Button>
        </>
      )}
    </div>
  );
}

// ── Fiche d'édition d'une case cochée (durée + désactivation) ────────────────

function EditSlotSheet({
  rule,
  tutorId,
  onClose,
}: {
  rule: TutorBookingRule;
  tutorId: string;
  onClose: () => void;
}) {
  const update = useUpdateTutorBookingRule(tutorId);
  const [duration, setDuration] = useState(rule.duration_minutes);

  const handleDurationChange = async (value: number) => {
    setDuration(value);
    try {
      await update.mutateAsync({ ruleId: rule.id, patch: { duration_minutes: value } });
    } catch {
      // best-effort ; le select revient à l'ancienne valeur via le refetch
    }
  };

  const handleDeactivate = async () => {
    await update.mutateAsync({ ruleId: rule.id, patch: { is_active: false } });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center">
      <div className="w-full max-w-sm rounded-t-2xl bg-white p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:rounded-2xl">
        <p className="font-display text-ink-900 text-base font-bold">
          {WEEKDAY_FULL_LABEL[rule.weekday]} {rule.start_time.slice(0, 5)}
        </p>
        <div className="mt-4">
          <FormField label="Durée" htmlFor="edit-duration">
            <select
              id="edit-duration"
              value={duration}
              onChange={(e) => handleDurationChange(Number(e.target.value))}
              className={SELECT_CLASS}
            >
              {DURATIONS.map((m) => (
                <option key={m} value={m}>
                  {m} min
                </option>
              ))}
            </select>
          </FormField>
        </div>
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
            Fermer
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeactivate}
            disabled={update.isPending}
            className="w-full sm:w-auto"
          >
            Désactiver ce créneau
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Tableau hebdomadaire ──────────────────────────────────────────────────────

function WeekGrid({
  tutorId,
  rules,
  canEdit,
  defaults,
}: {
  tutorId: string;
  rules: TutorBookingRule[];
  canEdit: boolean;
  defaults: {
    level: SchoolLevel;
    chapter_id: string | null;
    capacity: number;
    price_cents: number;
    kind: RendezVousKind;
  };
}) {
  const create = useCreateTutorBookingRule();
  const update = useUpdateTutorBookingRule(tutorId);
  const [editing, setEditing] = useState<TutorBookingRule | null>(null);

  const ruleByCell = useMemo(() => {
    const map = new Map<string, TutorBookingRule>();
    for (const r of rules) map.set(`${r.weekday}-${r.start_time}`, r);
    return map;
  }, [rules]);

  const handleCellClick = async (weekday: number, startTime: string) => {
    if (!canEdit) return;
    const existing = ruleByCell.get(`${weekday}-${startTime}`);
    if (existing?.is_active) {
      setEditing(existing);
      return;
    }
    if (existing && !existing.is_active) {
      await update.mutateAsync({ ruleId: existing.id, patch: { is_active: true } });
      return;
    }
    await create.mutateAsync({
      tutor_id: tutorId,
      weekday,
      start_time: startTime,
      duration_minutes: 60,
      level: defaults.level,
      chapter_id: defaults.chapter_id,
      capacity: defaults.capacity,
      price_cents: defaults.price_cents,
      kind: defaults.kind,
    });
  };

  return (
    <div className="border-ink-100 overflow-x-auto rounded-2xl border bg-white">
      <table className="w-full border-collapse text-center">
        <thead>
          <tr>
            <th className="bg-ink-50 text-ink-500 sticky left-0 z-10 w-14 border-b p-2 text-[11px] font-semibold" />
            {DAYS.map((d) => (
              <th
                key={d.weekday}
                className="border-ink-100 text-ink-500 min-w-11 border-b p-2 text-[11px] font-semibold"
              >
                {d.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {HOURS.map((hour) => {
            const startTime = hourStartTime(hour);
            return (
              <tr key={hour}>
                <td className="bg-ink-50 text-ink-500 sticky left-0 z-10 w-14 border-b p-2 text-[11px] font-semibold tabular-nums">
                  {hour}h
                </td>
                {DAYS.map((d) => {
                  const rule = ruleByCell.get(`${d.weekday}-${startTime}`);
                  const active = Boolean(rule?.is_active);
                  const customDuration = active && rule!.duration_minutes !== 60;
                  return (
                    <td key={d.weekday} className="border-ink-100 border-b p-1">
                      <button
                        type="button"
                        disabled={!canEdit}
                        onClick={() => handleCellClick(d.weekday, startTime)}
                        aria-label={`${d.label} ${hour}h${active ? ' (activé)' : ''}`}
                        aria-pressed={active}
                        className={cn(
                          'mx-auto flex h-11 w-11 flex-col items-center justify-center rounded-lg transition-colors active:scale-95',
                          'disabled:cursor-not-allowed disabled:opacity-40',
                          active
                            ? 'bg-primary-500 text-white'
                            : 'bg-ink-50 text-ink-300 hover:bg-ink-100',
                        )}
                      >
                        {active ? <Check className="h-4 w-4" /> : null}
                        {customDuration && (
                          <span className="text-[9px] font-semibold leading-none">
                            {rule!.duration_minutes}′
                          </span>
                        )}
                      </button>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {editing && (
        <EditSlotSheet rule={editing} tutorId={tutorId} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export function TutorBookingSlotsPage() {
  const { profile } = useAuth();
  const tutorId = profile?.id;
  const { data: defaults } = useTutorBookingDefaults(tutorId);
  const { data: rules = [] } = useTutorBookingRules(tutorId);
  const { data: slots = [] } = useTutorBookingSlots(tutorId);
  const cancelSlot = useCancelTutorBookingSlot(tutorId ?? '');

  const upcomingOpenSlots = useMemo(
    () => slots.filter((s) => s.status === 'open' && new Date(s.starts_at).getTime() > Date.now()),
    [slots],
  );

  const canEdit = Boolean(defaults?.level && defaults?.price_cents != null);

  // Source UNIQUE du kind courant, partagée par CourseProfilePanel et
  // WeekGrid — avant, WeekGrid dérivait le kind de `rules[0]?.kind`, complètement
  // déconnecté du toggle du panneau : un tuteur sans règle existante qui
  // choisissait "étude" voyait ses créneaux créés en 'cours' (prix forcé à 0
  // + kind='cours' → viole la contrainte DB, insert silencieusement en échec).
  // Hydraté UNE fois depuis les règles existantes (tuteur revenant), puis
  // piloté par le toggle local.
  const [kind, setKind] = useState<'cours' | 'etude'>('cours');
  const kindHydratedRef = useRef(false);
  useEffect(() => {
    if (!kindHydratedRef.current && rules.length > 0) {
      setKind(rules[0]!.kind === 'etude' ? 'etude' : 'cours');
      kindHydratedRef.current = true;
    }
  }, [rules]);

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-8">
      <h1 className="font-display text-ink-900 text-2xl font-extrabold tracking-tight">
        Rendez-vous
      </h1>
      <TutorBookingTabs />

      <div className="space-y-6">
        {tutorId && (
          <section className="space-y-3">
            <CourseProfilePanel tutorId={tutorId} kind={kind} onKindChange={setKind} />

            <div>
              <p className="text-ink-400 mb-2 text-xs font-semibold uppercase tracking-wider">
                Disponibilités hebdomadaires
              </p>
              {!canEdit && (
                <p className="text-accent-600 mb-2 font-sans text-xs">
                  Renseigne et enregistre ton profil ci-dessus pour activer des créneaux.
                </p>
              )}
              <WeekGrid
                tutorId={tutorId}
                rules={rules}
                canEdit={canEdit}
                defaults={
                  canEdit
                    ? {
                        level: defaults!.level!,
                        chapter_id: defaults!.chapter_id,
                        capacity: defaults!.capacity,
                        price_cents: defaults!.price_cents!,
                        kind,
                      }
                    : {
                        level: '3e' as SchoolLevel,
                        chapter_id: null,
                        capacity: 1,
                        price_cents: 2000,
                        kind: 'cours',
                      }
                }
              />
            </div>
          </section>
        )}

        <section className="space-y-3">
          <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
            Créneaux à venir ({upcomingOpenSlots.length})
          </p>
          {upcomingOpenSlots.length === 0 && (
            <p className="text-ink-500 font-sans text-sm">
              Aucun créneau matérialisé pour l’instant (la matérialisation tourne toutes les
              heures).
            </p>
          )}
          <div className="space-y-2">
            {upcomingOpenSlots.map((slot) => {
              const confirmed = slot.reservations.filter((r) => r.status === 'confirmed').length;
              const pending = slot.reservations.filter(
                (r) => r.status === 'pending_payment',
              ).length;
              return (
                <div
                  key={slot.id}
                  className="border-ink-100 flex items-center gap-3 rounded-2xl border bg-white p-4"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-ink-900 text-sm font-semibold capitalize">
                      {formatSlotDate(slot.starts_at)}
                    </p>
                    <p className="text-ink-500 mt-0.5 text-xs">
                      {confirmed}/{slot.capacity} confirmée{confirmed > 1 ? 's' : ''}
                      {pending > 0 ? ` · ${pending} en attente de paiement` : ''} ·{' '}
                      {formatPrice(slot.price_cents)}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (
                        window.confirm(
                          'Annuler ce créneau ? Les élèves déjà payés seront remboursés.',
                        )
                      ) {
                        cancelSlot.mutate(slot.id);
                      }
                    }}
                    disabled={cancelSlot.isPending}
                  >
                    Annuler
                  </Button>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
